<template>
    <v-container fluid>
      <!-- Linha de cabeçalho com métricas rápidas -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3" v-for="(metric, index) in quickMetrics" :key="index">
          <v-card class="metric-card" :color="metric.color" dark>
            <v-card-text class="d-flex align-center">
              <v-icon large class="mr-3">{{ metric.icon }}</v-icon>
              <div>
                <div class="metric-title">{{ metric.title }}</div>
                <div class="metric-value">{{ metric.value }}</div>
                <div class="metric-change" :class="metric.trend === 'up' ? 'success--text' : 'error--text'">
                  <v-icon small>{{ metric.trend === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                  {{ metric.change }}%
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Linha principal de gráficos -->
      <v-row>
        <!-- Gráfico de status de atendimentos -->
        <v-col cols="12" lg="6">
          <v-card class="chart-card">
            <v-card-title class="d-flex justify-space-between align-center">
              <div>
                <v-icon left color="primary">mdi-chart-donut</v-icon>
                Status dos Atendimentos
              </div>
              <v-btn 
                small
                outlined
                color="primary"
                :loading="loadingStatus"
                @click="fetchStatusData"
              >
                <v-icon small left>mdi-refresh</v-icon>
                Atualizar
              </v-btn>
            </v-card-title>
            <v-card-text>
              <apexchart 
                type="donut" 
                height="350" 
                :options="statusChartOptions" 
                :series="statusSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Gráfico de senhas por hora -->
        <v-col cols="12" lg="6">
          <v-card class="chart-card">
            <v-card-title class="d-flex justify-space-between align-center">
              <div>
                <v-icon left color="primary">mdi-chart-line</v-icon>
                Senhas por Hora (Hoje vs Ontem)
              </div>
              <div>
                <v-chip small class="mr-2" color="blue" text-color="white">
                  <v-icon small left>mdi-circle</v-icon>
                  Hoje
                </v-chip>
                <v-chip small color="green" text-color="white">
                  <v-icon small left>mdi-circle</v-icon>
                  Ontem
                </v-chip>
              </div>
            </v-card-title>
            <v-card-text>
              <apexchart 
                type="line" 
                height="350" 
                :options="hourlyChartOptions" 
                :series="hourlySeries"
              ></apexchart>
              <div class="update-time">
                <v-icon small>mdi-clock-outline</v-icon>
                Atualizado {{ lastUpdateTime }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Segunda linha de gráficos -->
      <v-row>
        <!-- Gráfico de especialidades -->
        <v-col cols="12" lg="6">
          <v-card class="chart-card">
            <v-card-title>
              <v-icon left color="primary">mdi-stethoscope</v-icon>
              Distribuição por Especialidade
            </v-card-title>
            <v-card-text>
              <apexchart 
                type="bar" 
                height="350" 
                :options="specialtyChartOptions" 
                :series="specialtySeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Gráfico de tempo médio de atendimento -->
        <v-col cols="12" lg="6">
          <v-card class="chart-card">
            <v-card-title>
              <v-icon left color="primary">mdi-timer</v-icon>
              Tempo Médio de Atendimento (minutos)
            </v-card-title>
            <v-card-text>
              <apexchart 
                type="radialBar" 
                height="350" 
                :options="timeChartOptions" 
                :series="timeSeries"
              ></apexchart>
              <div class="time-metrics">
                <v-row>
                  <v-col cols="6" v-for="(metric, index) in timeMetrics" :key="index">
                    <div class="time-metric">
                      <div class="time-value">{{ metric.value }}</div>
                      <div class="time-label">{{ metric.label }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabela de atendimentos recentes -->
      <v-row>
        <v-col cols="12">
          <v-card class="data-table">
            <v-card-title>
              <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
              Atendimentos Recentes
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Pesquisar"
                single-line
                hide-details
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="headers"
              :items="recentAttendances"
              :search="search"
              :loading="loadingTable"
              loading-text="Carregando... Por favor aguarde"
            >
              <template v-slot:item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" small dark>
                  {{ formatStatus(item.status) }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click="viewDetails(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import administradorUnidadeService from '../../services/administradorUnidade.service'
import socketService from '../../services/socket.service'
import { getToken } from '../../store/auth'

export default {
  name: 'MedicalDashboard',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      // Dados do dashboard
      loadingStatus: false,
      loadingHourly: false,
      loadingTable: false,
      lastUpdateTime: 'agora',
      search: '',
      
      // Métricas rápidas
      quickMetrics: [
        { title: 'Atendimentos Hoje', value: 0, change: 0, trend: 'up', icon: 'mdi-calendar-today', color: 'primary' },
        { title: 'Médicos Online', value: 0, change: 0, trend: 'up', icon: 'mdi-account-group', color: 'green' },
        { title: 'Tempo Médio', value: '12m', change: -5, trend: 'down', icon: 'mdi-timer', color: 'orange' },
        { title: 'Satisfação', value: '92%', change: 2, trend: 'up', icon: 'mdi-emoticon-happy', color: 'purple' }
      ],
      
      // Gráfico de status
      statusSeries: [],
      statusChartOptions: {
        chart: {
          type: 'donut',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        labels: [
          'Pendente',
          'Chamado',
          'Em atendimento',
          'Atendido',
          'Cancelado',
          'Não compareceu'
        ],
        colors: ['#FFA726', '#42A5F5', '#66BB6A', '#4CAF50', '#EF5350', '#78909C'],
        legend: {
          position: 'right'
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '16px'
                }
              }
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      
      // Gráfico por hora
      hourlySeries: [{
        name: 'Hoje',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      }, {
        name: 'Ontem',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      }],
      hourlyChartOptions: {
        chart: {
          type: 'line',
          height: 350,
          animations: {
            enabled: true
          },
          toolbar: {
            show: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        colors: ['#42A5F5', '#66BB6A'],
        xaxis: {
          categories: ['6h', '7h', '8h', '9h', '10h', '11h', '12h', '13h', '14h', '15h', '16h', '17h'],
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Número de senhas'
          },
          min: 0
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val) {
              return val + (val === 1 ? ' senha' : ' senhas')
            }
          }
        },
        markers: {
          size: 5,
          hover: {
            size: 7
          }
        }
      },
      
      // Gráfico de especialidades
      specialtySeries: [{
        name: 'Atendimentos',
        data: []
      }],
      specialtyChartOptions: {
        chart: {
          type: 'bar',
          height: 350
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
          }
        },
        colors: ['#4CAF50'],
        dataLabels: {
          enabled: true
        },
        xaxis: {
          categories: [],
          title: {
            text: 'Número de atendimentos'
          }
        }
      },
      
      // Gráfico de tempo
      timeSeries: [75],
      timeChartOptions: {
        chart: {
          type: 'radialBar',
          height: 350
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: {
              margin: 0,
              size: '70%',
            },
            dataLabels: {
              name: {
                offsetY: -10,
                color: '#333',
                fontSize: '13px'
              },
              value: {
                color: '#333',
                fontSize: '30px',
                show: true
              }
            },
            track: {
              background: '#e0e0e0',
              strokeWidth: '97%',
              margin: 5,
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            shadeIntensity: 0.15,
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 50, 65, 91]
          },
        },
        labels: ['Tempo médio'],
      },
      timeMetrics: [
        { label: 'Mínimo', value: '5m' },
        { label: 'Máximo', value: '28m' },
        { label: 'Meta', value: '15m' },
        { label: 'Desvio', value: '3.2m' }
      ],
      
      // Tabela de atendimentos
      headers: [
        { text: 'Paciente', value: 'patient' },
        { text: 'Médico', value: 'doctor' },
        { text: 'Especialidade', value: 'specialty' },
        { text: 'Hora', value: 'time' },
        { text: 'Status', value: 'status' },
        { text: 'Ações', value: 'actions', sortable: false }
      ],
      recentAttendances: []
    }
  },
  
  mounted() {
    this.user = this.$store.getters.usuario
    if (this.user.tipo != 5) {
      this.$router.push('/login')
      return
    }
    
    this.initSocket()
    this.loadAllData()
  },
  
  methods: {
    // Inicialização
    loadAllData() {
      this.fetchStatusData()
      this.fetchHourlyData()
      this.fetchSpecialtyData()
      this.fetchRecentAttendances()
      this.fetchQuickMetrics()
    },
    
    initSocket() {
      this.socket = io.connect(socketService.SOCKET_URL, {
        query: {
          token: getToken()
        }
      })
      
      this.socket.on('novaFichaGerada', this.handleNewTicket)
      this.socket.on('atendimentoAtualizado', this.handleAttendanceUpdate)
    },
    
    // Manipuladores de socket
    handleNewTicket(data) {
      if (data.idUnidade === this.user.idUnidade) {
        this.fetchHourlyData()
        this.fetchQuickMetrics()
        this.updateLastUpdateTime()
      }
    },
    
    handleAttendanceUpdate(data) {
      if (data.idUnidade === this.user.idUnidade) {
        this.fetchStatusData()
        this.fetchRecentAttendances()
        this.updateLastUpdateTime()
      }
    },
    
    // Atualização de dados
    updateLastUpdateTime() {
      const now = new Date()
      this.lastUpdateTime = now.toLocaleTimeString()
    },
    
    // Busca de dados
    async fetchStatusData() {
      this.loadingStatus = true
      try {
        const response = await administradorUnidadeService.administradorUnidade.retornarQuantAtendimentoStatus()
        this.processStatusData(response.data)
      } catch (error) {
        console.error('Erro ao buscar status:', error)
      } finally {
        this.loadingStatus = false
      }
    },
    
    processStatusData(data) {
      const statusMap = {
        'pendente': 0,
        'chamando': 1,
        'emAtendimentoAtendente': 2,
        'atendido': 3,
        'cancelado': 4,
        'naoAtendido': 5
      }
      
      const series = [0, 0, 0, 0, 0, 0]
      
      data.forEach(item => {
        if (statusMap[item.status] !== undefined) {
          series[statusMap[item.status]] = item.quantAtendimento
        }
      })
      
      this.statusSeries = series
    },
    
    async fetchHourlyData() {
      this.loadingHourly = true
      try {
        const response = await administradorUnidadeService.administradorUnidade.retornarQuantSenhas24Horas()
        this.processHourlyData(response.data)
      } catch (error) {
        console.error('Erro ao buscar dados por hora:', error)
      } finally {
        this.loadingHourly = false
        this.updateLastUpdateTime()
      }
    },
    
    processHourlyData(data) {
      const today = new Date()
      const todayDay = today.getDate()
      
      // Reset arrays
      const hoje = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      const ontem = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      
      if (data.length) {
        data.forEach(item => {
          const hourIndex = item.horas - 6
          if (hourIndex >= 0 && hourIndex < 12) {
            if (item.dia === todayDay) {
              hoje[hourIndex] = item.quantAtendimentos
            } else {
              ontem[hourIndex] = item.quantAtendimentos
            }
          }
        })
      }
      
      this.hourlySeries = [
        { name: 'Hoje', data: hoje },
        { name: 'Ontem', data: ontem }
      ]
    },
    
    async fetchSpecialtyData() {
      try {
        const response = await administradorUnidadeService.administradorUnidade.retornarEspecialidades()
        this.processSpecialtyData(response.data)
      } catch (error) {
        console.error('Erro ao buscar especialidades:', error)
      }
    },
    
    processSpecialtyData(data) {
      const categories = []
      const seriesData = []
      
      data.forEach(item => {
        categories.push(item.nome)
        seriesData.push(item.quantidade)
      })
      
      this.specialtySeries = [{ name: 'Atendimentos', data: seriesData }]
      this.specialtyChartOptions = {
        ...this.specialtyChartOptions,
        xaxis: {
          ...this.specialtyChartOptions.xaxis,
          categories: categories
        }
      }
    },
    
    async fetchRecentAttendances() {
      this.loadingTable = true
      try {
        const response = await administradorUnidadeService.administradorUnidade.retornarAtendimentosRecentes()
        this.recentAttendances = response.data.map(item => ({
          patient: item.paciente,
          doctor: item.medico,
          specialty: item.especialidade,
          time: this.formatTime(item.hora),
          status: item.status
        }))
      } catch (error) {
        console.error('Erro ao buscar atendimentos recentes:', error)
      } finally {
        this.loadingTable = false
      }
    },
    
    async fetchQuickMetrics() {
      try {
        const [dashboardRes, onlineRes] = await Promise.all([
          administradorUnidadeService.administradorUnidade.retornarDashboard(),
          administradorUnidadeService.administradorUnidade.retornarQuantFuncionariosOnline()
        ])
        
        const dashboardData = dashboardRes.data[0]
        const onlineData = onlineRes.data
        
        this.quickMetrics = [
          { 
            title: 'Atendimentos Hoje', 
            value: dashboardData.atendimentosHoje || 0,
            change: 5, // Simulado - deveria vir da API
            trend: 'up',
            icon: 'mdi-calendar-today',
            color: 'primary'
          },
          { 
            title: 'Médicos Online', 
            value: onlineData.medico || 0,
            change: 0,
            trend: onlineData.medico > this.quickMetrics[1].value ? 'up' : 'down',
            icon: 'mdi-account-group',
            color: 'green'
          },
          { 
            title: 'Tempo Médio', 
            value: dashboardData.tempoMedio + 'm' || '0m',
            change: -2,
            trend: 'down',
            icon: 'mdi-timer',
            color: 'orange'
          },
          { 
            title: 'Satisfação', 
            value: dashboardData.satisfacao + '%' || '0%',
            change: 1,
            trend: 'up',
            icon: 'mdi-emoticon-happy',
            color: 'purple'
          }
        ]
      } catch (error) {
        console.error('Erro ao buscar métricas rápidas:', error)
      }
    },
    
    // Utilitários
    formatTime(timeString) {
      if (!timeString) return ''
      const [hours, minutes] = timeString.split(':')
      return `${hours}h${minutes}`
    },
    
    formatStatus(status) {
      const statusMap = {
        'pendente': 'Pendente',
        'chamando': 'Chamando',
        'emAtendimentoAtendente': 'Em atendimento',
        'atendido': 'Atendido',
        'cancelado': 'Cancelado',
        'naoAtendido': 'Não compareceu'
      }
      return statusMap[status] || status
    },
    
    getStatusColor(status) {
      const colorMap = {
        'pendente': 'orange',
        'chamando': 'blue',
        'emAtendimentoAtendente': 'light-blue',
        'atendido': 'green',
        'cancelado': 'red',
        'naoAtendido': 'grey'
      }
      return colorMap[status] || 'grey'
    },
    
    viewDetails(item) {
      console.log('Visualizar detalhes:', item)
      // Implemente a navegação para detalhes aqui
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f5f7fa;
  padding: 20px;
}

.metric-card {
  border-radius: 10px;
  transition: transform 0.3s ease;
  height: 100%;
}

.metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.metric-title {
  font-size: 14px;
  opacity: 0.8;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
}

.metric-change {
  font-size: 12px;
  display: flex;
  align-items: center;
}

.chart-card {
  border-radius: 10px;
  margin-bottom: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 10px 15px rgba(0,0,0,0.1);
}

.update-time {
  font-size: 12px;
  color: #666;
  text-align: right;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.update-time i {
  font-size: 14px;
  margin-right: 5px;
}

.time-metrics {
  margin-top: 20px;
}

.time-metric {
  text-align: center;
  padding: 10px;
  border-radius: 8px;
  background-color: #f5f7fa;
}

.time-value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.time-label {
  font-size: 12px;
  color: #666;
}

.data-table {
  border-radius: 10px;
  overflow: hidden;
}

/* Responsividade */
@media (max-width: 960px) {
  .metric-value {
    font-size: 20px;
  }
}

@media (max-width: 600px) {
  .dashboard-container {
    padding: 10px;
  }
  
  .metric-title {
    font-size: 12px;
  }
  
  .metric-value {
    font-size: 18px;
  }
}
</style>